import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { 
  CheckCircle, Calendar, Clock, User, Eye, Printer, RotateCcw, 
  Ban, Briefcase, RefreshCw, Search, FileText, XCircle, MoreVertical, Edit, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import QuotePrintView from './QuotePrintView';
import WorkflowDiagram from './WorkflowDiagram';
import ChangeStatusDialog from './ChangeStatusDialog';
import StatusBadge from './StatusBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrdersManagement = ({ getAuthHeaders }) => {
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [services, setServices] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isPrintViewOpen, setIsPrintViewOpen] = useState(false);
  const [printOrder, setPrintOrder] = useState(null);
  
  // Scheduling dialog state
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [scheduleData, setScheduleData] = useState({
    scheduled_start: '',
    scheduled_end: '',
    manual_duration: '', // Allow manual duration override
    scheduled_location: '',
    address_1: '',
    address_2: '',
    address_3: '',
    city: '',
    county: '',
    postcode: '',
    location_type: 'mobile',
    flexible_timing: false,
    special_instructions: ''
  });

  // New reversible workflow states
  const [changeStatusDialogOpen, setChangeStatusDialogOpen] = useState(false);
  const [orderToChangeStatus, setOrderToChangeStatus] = useState(null);

  // Available statuses for orders
  const orderStatuses = ['active', 'on_hold', 'cancelled'];

  useEffect(() => {
    fetchOrders();
    fetchCustomers();
    fetchContacts();
    fetchServices();
    fetchVehicles();
  }, []);

  // Check if we need to auto-open schedule dialog for a specific quote
  useEffect(() => {
    if (location.state?.openScheduleForQuote && orders.length > 0) {
      const quoteId = location.state.openScheduleForQuote;
      const order = orders.find(o => o.id === quoteId);
      if (order) {
        openScheduleDialog(order);
        // Clear the state to prevent reopening on refresh
        window.history.replaceState({}, document.title);
      }
    }
  }, [orders, location.state]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchOrders();
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      // Fetch only accepted quotes (orders)
      const response = await axios.get(`${API}/quotes?status=accepted`, getAuthHeaders());
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to fetch orders');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, getAuthHeaders());
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`, getAuthHeaders());
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`, getAuthHeaders());
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles`, getAuthHeaders());
      setVehicles(response.data);
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
    }
  };

  const openScheduleDialog = (order) => {
    console.log('Opening schedule dialog for order:', order);
    console.log('Order items:', order.items);
    const totalDuration = calculateTotalDurationWithLookup(order);
    
    // Get customer for auto-population
    const customer = customers.find(c => c.id === order.customer_id);
    
    setSelectedOrder(order);
    setScheduleData({
      scheduled_start: '',
      scheduled_end: '',
      manual_duration: totalDuration > 0 ? totalDuration : '', // Pre-fill with calculated duration
      scheduled_location: '',
      address_1: customer?.address_1 || '',
      address_2: customer?.address_2 || '',
      address_3: customer?.address_3 || '',
      city: customer?.city || '',
      county: customer?.county || '',
      postcode: customer?.postcode || '',
      location_type: 'mobile',
      flexible_timing: false,
      special_instructions: ''
    });
    setIsScheduleDialogOpen(true);
  };

  // Calculate total duration with service lookup fallback
  const calculateTotalDurationWithLookup = (order) => {
    if (!order || !order.items) {
      console.log('No order or no items');
      return 0;
    }
    
    console.log('Calculating duration for items with service lookup...');
    const total = order.items.reduce((sum, item) => {
      let itemDuration = item.duration || 0;
      
      // If duration is 0 and we have a service_id, try to fetch from current service definition
      if (itemDuration === 0 && item.service_id && services.length > 0) {
        const service = services.find(s => s.id === item.service_id);
        if (service && service.default_duration_minutes) {
          itemDuration = service.default_duration_minutes;
          console.log(`Found duration ${itemDuration} from service ${service.name} for item ${item.description}`);
        }
      }
      
      const itemQuantity = item.quantity || 1;
      const subtotal = itemDuration * itemQuantity;
      console.log(`Item: ${item.description}, Duration: ${itemDuration}, Quantity: ${itemQuantity}, Subtotal: ${subtotal}`);
      return sum + subtotal;
    }, 0);
    
    console.log('Total duration calculated with lookup:', total, 'minutes for order:', order.quote_number);
    return total;
  };

  // Calculate total duration from order items in minutes
  const calculateTotalDuration = (order) => {
    if (!order || !order.items) {
      console.log('No order or no items');
      return 0;
    }
    console.log('Calculating duration for items:', JSON.stringify(order.items, null, 2));
    const total = order.items.reduce((sum, item) => {
      const itemDuration = item.duration || 0;
      const itemQuantity = item.quantity || 1;
      console.log(`Item: ${item.description}, Duration: ${itemDuration}, Quantity: ${itemQuantity}, Subtotal: ${itemDuration * itemQuantity}`);
      return sum + (itemDuration * itemQuantity);
    }, 0);
    console.log('Total duration calculated:', total, 'minutes for order:', order.quote_number);
    return total;
  };

  // Handle start time change - simplified to just update state
  const handleStartTimeChange = (startDateTime) => {
    console.log('Start time changed to:', startDateTime);
    setScheduleData(prev => ({
      ...prev,
      scheduled_start: startDateTime
    }));
    
    // Auto-calculate end time if we have duration
    if (startDateTime && scheduleData.manual_duration) {
      calculateAndSetEndTime(startDateTime, scheduleData.manual_duration);
    }
  };
  
  // Calculate end time based on start time and duration
  const calculateAndSetEndTime = (startDateTime, durationMinutes) => {
    if (!startDateTime || !durationMinutes) return;
    
    const startDate = new Date(startDateTime);
    const endDate = new Date(startDate.getTime() + parseInt(durationMinutes) * 60000);
    
    const year = endDate.getFullYear();
    const month = String(endDate.getMonth() + 1).padStart(2, '0');
    const day = String(endDate.getDate()).padStart(2, '0');
    const hours = String(endDate.getHours()).padStart(2, '0');
    const minutes = String(endDate.getMinutes()).padStart(2, '0');
    const endDateString = `${year}-${month}-${day}T${hours}:${minutes}`;
    
    console.log('Calculated end time:', endDateString);
    setScheduleData(prev => ({
      ...prev,
      scheduled_end: endDateString
    }));
  };

  // Handle duration change
  const handleDurationChange = (duration) => {
    setScheduleData(prev => ({
      ...prev,
      manual_duration: duration
    }));
    
    // Auto-calculate end time if we have start time
    if (scheduleData.scheduled_start && duration) {
      calculateAndSetEndTime(scheduleData.scheduled_start, duration);
    }
  };

  const handleScheduleOrder = async () => {
    if (!selectedOrder) return;
    
    if (!scheduleData.scheduled_start && !scheduleData.flexible_timing) {
      toast.error('Please select a start date and time');
      return;
    }

    try {
      // Build query params with all scheduling data
      const params = new URLSearchParams();
      if (scheduleData.scheduled_start) params.append('scheduled_start', scheduleData.scheduled_start);
      if (scheduleData.scheduled_end) params.append('scheduled_end', scheduleData.scheduled_end);
      if (scheduleData.scheduled_location) params.append('scheduled_location', scheduleData.scheduled_location);
      // Structured address fields
      if (scheduleData.address_1) params.append('address_1', scheduleData.address_1);
      if (scheduleData.address_2) params.append('address_2', scheduleData.address_2);
      if (scheduleData.address_3) params.append('address_3', scheduleData.address_3);
      if (scheduleData.city) params.append('city', scheduleData.city);
      if (scheduleData.county) params.append('county', scheduleData.county);
      if (scheduleData.postcode) params.append('postcode', scheduleData.postcode);
      if (scheduleData.location_type) params.append('location_type', scheduleData.location_type);
      if (scheduleData.flexible_timing) params.append('flexible_timing', scheduleData.flexible_timing);
      if (scheduleData.special_instructions) params.append('special_instructions', scheduleData.special_instructions);
      
      // Convert the order to a job with full scheduling data
      await axios.post(
        `${API}/quotes/${selectedOrder.id}/convert-to-jobs?${params.toString()}`, 
        {}, 
        getAuthHeaders()
      );
      
      toast.success('Order scheduled successfully and moved to Jobs');
      setIsScheduleDialogOpen(false);
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to schedule order';
      toast.error(errorMessage);
    }
  };

  const handleRevertToQuote = async (orderId) => {
    try {
      await axios.put(`${API}/quotes/${orderId}/status`, {
        status: 'sent',
        reason: 'Reverted from order back to quote'
      }, getAuthHeaders());
      
      toast.success('Order reverted to quote successfully');
      fetchOrders();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to revert order';
      toast.error(errorMessage);
    }
  };

  const handleCancelOrder = async (orderId) => {
    try {
      await axios.put(`${API}/quotes/${orderId}/status`, {
        status: 'cancelled',
        reason: 'Order cancelled by user'
      }, getAuthHeaders());
      
      toast.success('Order cancelled successfully');
      fetchOrders();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to cancel order';
      toast.error(errorMessage);
    }
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  // Handle status change with reversibility
  const handleChangeStatus = async (newStatus, reason) => {
    if (!orderToChangeStatus) return;
    
    try {
      await axios.put(
        `${API}/quotes/${orderToChangeStatus.id}`, 
        { 
          status: newStatus,
          status_change_reason: reason || undefined
        }, 
        getAuthHeaders()
      );
      
      toast.success(`Order status changed to ${newStatus}`);
      setChangeStatusDialogOpen(false);
      setOrderToChangeStatus(null);
      fetchOrders();
    } catch (error) {
      console.error('Status change error:', error);
      toast.error('Failed to change status');
    }
  };

  const openChangeStatusDialog = (order) => {
    setOrderToChangeStatus(order);
    setChangeStatusDialogOpen(true);
  };

  const filteredOrders = orders.filter(order => {
    if (searchTerm) {
      const customer = customers.find(c => c.id === order.customer_id);
      const customerName = customer?.name || '';
      return (
        order.quote_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customerName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return true;
  });

  // Helper function to get vehicles for a job
  const getJobVehicles = (job) => {
    if (!job.vehicle_ids || job.vehicle_ids.length === 0) return [];
    return job.vehicle_ids
      .map(vehicleId => vehicles.find(v => v.id === vehicleId))
      .filter(v => v); // Remove any undefined entries
  };

  // Helper function to get duration
  const getDuration = (job) => {
    if (job.scheduled_start && job.scheduled_end) {
      const start = new Date(job.scheduled_start);
      const end = new Date(job.scheduled_end);
      return Math.round((end - start) / (1000 * 60)); // Duration in minutes
    }
    return 0;
  };

  // Generate HTML for printing
  const generatePrintHTML = (job) => {
    const customer = customers.find(c => c.id === job.customer_id);
    const contact = contacts.find(c => c.id === job.contact_id);
    const jobVehicles = getJobVehicles(job);
    
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Job Details - ${job.job_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; margin: 0; }
            h1 { font-size: 24px; margin-bottom: 20px; }
            h3 { font-size: 16px; font-weight: bold; margin-top: 20px; margin-bottom: 10px; }
            .section { margin-bottom: 20px; padding: 15px; border: 1px solid #e5e7eb; border-radius: 8px; }
            .bg-blue { background-color: #eff6ff; }
            .bg-purple { background-color: #faf5ff; }
            .bg-green { background-color: #f0fdf4; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
            .text-sm { font-size: 14px; }
            .font-medium { font-weight: 500; }
            .font-bold { font-weight: 700; }
            .text-gray { color: #6b7280; }
            .vehicle-item { background: white; padding: 12px; margin-bottom: 10px; border: 1px solid #e5e7eb; border-radius: 4px; }
            .service-item { background: #f9fafb; padding: 12px; margin-bottom: 8px; border-left: 4px solid #3b82f6; }
            .flex-between { display: flex; justify-content: space-between; }
            .border-top { border-top: 1px solid #e5e7eb; padding-top: 12px; margin-top: 12px; }
          </style>
        </head>
        <body>
          <h1>Job Details - ${job.job_number}</h1>
          
          <div class="section bg-blue">
            <h3>Customer Information</h3>
            <div class="grid text-sm">
              <div>
                <span class="text-gray">Customer:</span>
                <div class="font-medium">${customer?.name || 'Unknown'}</div>
              </div>
              <div>
                <span class="text-gray">Contact:</span>
                <div class="font-medium">${contact ? `${contact.first_name} ${contact.last_name}` : 'N/A'}</div>
              </div>
            </div>
          </div>
          
          ${jobVehicles.length > 0 ? `
            <div class="section bg-purple">
              <h3>Vehicles for this Job</h3>
              ${jobVehicles.map(v => `
                <div class="vehicle-item">
                  <div class="font-bold">${v.registration_number}</div>
                  <div class="text-sm">${v.make} ${v.model} ${v.year ? `(${v.year})` : ''}</div>
                  ${v.color ? `<div class="text-sm text-gray">Color: ${v.color}</div>` : ''}
                </div>
              `).join('')}
            </div>
          ` : ''}
          
          <div class="section">
            <h3>Scheduling Details</h3>
            <div class="grid text-sm">
              <div>
                <span class="text-gray">Status:</span>
                <div class="font-medium" style="text-transform: capitalize;">${job.status.replace('_', ' ')}</div>
              </div>
              <div>
                <span class="text-gray">Date:</span>
                <div class="font-medium">${job.scheduled_start ? new Date(job.scheduled_start).toLocaleDateString('en-GB') : 'Not scheduled'}</div>
              </div>
              <div>
                <span class="text-gray">Start Time:</span>
                <div class="font-medium">${job.flexible_timing ? 'Time Flexible' : job.scheduled_start ? new Date(job.scheduled_start).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'Not set'}</div>
              </div>
              <div>
                <span class="text-gray">End Time:</span>
                <div class="font-medium">${job.scheduled_end ? new Date(job.scheduled_end).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'N/A'}</div>
              </div>
              <div style="grid-column: span 2;">
                <span class="text-gray">Duration:</span>
                <div class="font-medium">${getDuration(job)} minutes</div>
              </div>
            </div>
          </div>
          
          <div class="section">
            <h3>Location Details</h3>
            <div class="text-sm">
              <div style="margin-bottom: 8px;">
                <span class="text-gray">Location Type:</span>
                <div class="font-medium" style="text-transform: capitalize;">${job.location_type}</div>
              </div>
              <div style="margin-bottom: 8px;">
                <span class="text-gray">Location Name:</span>
                <div class="font-medium">${job.scheduled_location || 'N/A'}</div>
              </div>
              <div class="border-top">
                <span class="text-gray font-bold">Address:</span>
                <div class="font-medium" style="margin-top: 8px;">
                  ${job.address_1 || job.city || job.postcode ? `
                    ${job.address_1 ? `<div>${job.address_1}</div>` : ''}
                    ${job.address_2 ? `<div>${job.address_2}</div>` : ''}
                    ${job.address_3 ? `<div>${job.address_3}</div>` : ''}
                    ${job.city ? `<div>${job.city}</div>` : ''}
                    ${job.county ? `<div>${job.county}</div>` : ''}
                    ${job.postcode ? `<div class="font-bold">${job.postcode}</div>` : ''}
                  ` : 'N/A'}
                </div>
              </div>
              <div class="border-top">
                <span class="text-gray">Special Instructions:</span>
                <div class="font-medium" style="margin-top: 4px;">${job.special_instructions || 'N/A'}</div>
              </div>
            </div>
          </div>
          
          ${job.items && job.items.length > 0 ? `
            <div class="section">
              <h3>Services / Items</h3>
              ${job.items.map(item => {
                const service = services.find(s => s.id === item.service_product_id);
                const itemVehicle = item.vehicle_id ? vehicles.find(v => v.id === item.vehicle_id) : null;
                return `
                  <div class="service-item">
                    <div class="flex-between">
                      <div style="flex: 1;">
                        <div class="font-medium text-sm">${service?.name || item.description || 'Service'}</div>
                        ${itemVehicle ? `
                          <div class="text-sm" style="color: #1d4ed8; margin: 4px 0;">
                            <span class="font-bold">${itemVehicle.registration_number}</span>
                            <span class="text-gray"> ${itemVehicle.make} ${itemVehicle.model}</span>
                          </div>
                        ` : ''}
                        <div class="text-sm text-gray">Qty: ${item.quantity} × £${item.unit_price?.toFixed(2)}</div>
                      </div>
                      <div class="font-bold" style="font-size: 18px; margin-left: 16px;">£${item.line_total?.toFixed(2)}</div>
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          ` : ''}
          
          <div class="section bg-green">
            <h3>Financial Summary</h3>
            <div class="text-sm">
              <div class="flex-between" style="margin-bottom: 8px;">
                <span class="text-gray">Subtotal:</span>
                <span class="font-medium">£${job.subtotal?.toFixed(2) || '0.00'}</span>
              </div>
              <div class="flex-between" style="margin-bottom: 8px;">
                <span class="text-gray">Tax:</span>
                <span class="font-medium">£${job.tax?.toFixed(2) || '0.00'}</span>
              </div>
              <div class="flex-between font-bold border-top" style="font-size: 18px;">
                <span>Total:</span>
                <span>£${job.total?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
          </div>
          
          ${job.notes ? `
            <div class="section">
              <h3>Notes</h3>
              <p class="text-sm">${job.notes}</p>
            </div>
          ` : ''}
        </body>
      </html>
    `;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>
            Orders
          </h2>
          <p className="text-gray-500">Accepted quotes awaiting scheduling</p>
        </div>
      </div>

      {/* Workflow Diagram */}
      <WorkflowDiagram currentStage="order" />

      {/* Stats Card */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Total Orders</p>
            <p className="text-2xl font-bold">{orders.length}</p>
          </div>
          <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Accepted
          </Badge>
        </div>
      </Card>

      {/* Search */}
      <Card className="p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </Card>

      {/* Orders Table */}
      {filteredOrders.length === 0 ? (
        <Card className="p-12 text-center">
          <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm
              ? 'No orders match your search.'
              : 'Orders will appear here when quotes are accepted.'
            }
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Accepted Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.map((order) => {
                const customer = customers.find(c => c.id === order.customer_id);
                return (
                  <TableRow key={order.id}>
                    <TableCell>
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                        {order.quote_number}
                      </code>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span>{customer?.name || 'Unknown Customer'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {order.contact_name ? (
                        <span className="text-sm">{order.contact_name}</span>
                      ) : (
                        <span className="text-gray-400 text-sm">No contact</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {order.vehicles && order.vehicles.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {order.vehicles.map((vehicle, idx) => (
                            <code key={idx} className="bg-blue-50 px-2 py-0.5 rounded text-xs font-mono">
                              {vehicle.registration_number}
                            </code>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">No vehicle</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-green-600">
                        {formatCurrency(order.total, order.currency)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {order.accepted_at ? (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-gray-400" />
                          <span className="text-sm">
                            {new Date(order.accepted_at).toLocaleDateString()}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {/* Primary Actions - Always Visible */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedOrder(order);
                            setIsViewDialogOpen(true);
                          }}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setPrintOrder(order);
                            setIsPrintViewOpen(true);
                          }}
                          title="Print/Email Order"
                          className="text-purple-600"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>

                        {/* Schedule Button - Prominent */}
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => openScheduleDialog(order)}
                          className="gap-1 bg-blue-600 hover:bg-blue-700"
                          title="Schedule Order"
                        >
                          <Calendar className="w-4 h-4" />
                          Schedule
                        </Button>

                        {/* Secondary Actions - Dropdown Menu */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem 
                              onClick={() => handleRevertToQuote(order.id)}
                              className="text-orange-600"
                            >
                              <RotateCcw className="w-4 h-4 mr-2" />
                              Revert to Quote
                            </DropdownMenuItem>

                            <DropdownMenuSeparator />
                            
                            <DropdownMenuItem 
                              onClick={() => handleCancelOrder(order.id)}
                              className="text-red-600"
                            >
                              <Ban className="w-4 h-4 mr-2" />
                              Cancel Order
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* View Order Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-7xl max-h-[95vh] overflow-y-auto w-[95vw]">
          <DialogHeader>
            <DialogTitle>
              Order Details - {selectedOrder?.quote_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Order Header */}
              <div className="grid grid-cols-2 gap-6 p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-medium mb-2">Order Information</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Accepted
                      </Badge>
                    </div>
                    <div>Total: {formatCurrency(selectedOrder.total, selectedOrder.currency)}</div>
                    <div>Accepted: {selectedOrder.accepted_at ? 
                      new Date(selectedOrder.accepted_at).toLocaleDateString() : '-'
                    }</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-medium mb-2">Customer</h3>
                  <div className="text-sm">
                    {customers.find(c => c.id === selectedOrder.customer_id)?.name || 'Unknown Customer'}
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div>
                <h3 className="font-medium mb-4">Order Items</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Discount</TableHead>
                      <TableHead>Tax</TableHead>
                      <TableHead>Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedOrder.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell>{formatCurrency(item.discount_value)}</TableCell>
                        <TableCell>{item.tax_rate}%</TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(item.line_total)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                <div className="mt-4 space-y-2 text-right">
                  <div>Subtotal: {formatCurrency(selectedOrder.subtotal)}</div>
                  <div>Tax: {formatCurrency(selectedOrder.tax_total)}</div>
                  <div className="font-bold text-lg">
                    Total: {formatCurrency(selectedOrder.total)}
                  </div>
                </div>
              </div>

              {/* Notes */}
              {(selectedOrder.notes_public || selectedOrder.notes_internal) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedOrder.notes_public && (
                    <div>
                      <h4 className="font-medium mb-2">Customer Notes</h4>
                      <div className="p-3 bg-gray-50 rounded text-sm">
                        {selectedOrder.notes_public}
                      </div>
                    </div>
                  )}
                  
                  {selectedOrder.notes_internal && (
                    <div>
                      <h4 className="font-medium mb-2">Internal Notes</h4>
                      <div className="p-3 bg-yellow-50 rounded text-sm">
                        {selectedOrder.notes_internal}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Schedule Order Dialog */}
      <Dialog open={isScheduleDialogOpen} onOpenChange={setIsScheduleDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Schedule Order</DialogTitle>
            <DialogDescription>
              Complete scheduling details to convert this order to a job
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="text-sm font-medium mb-1">Order: {selectedOrder.quote_number}</div>
                <div className="text-sm text-gray-600">
                  Customer: {customers.find(c => c.id === selectedOrder.customer_id)?.name || 'Unknown'}
                </div>
                <div className="text-sm text-gray-600">
                  Total: {formatCurrency(selectedOrder.total, selectedOrder.currency)}
                </div>
              </div>

              {/* Date & Time Section */}
              <div className="space-y-3">
                <h3 className="font-medium text-sm">Date & Time</h3>
                
                <div className="flex items-center space-x-2 p-3 bg-orange-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="flexible_timing_order"
                    checked={scheduleData.flexible_timing}
                    onChange={(e) => setScheduleData({ 
                      ...scheduleData, 
                      flexible_timing: e.target.checked
                    })}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <Label htmlFor="flexible_timing_order" className="cursor-pointer font-normal">
                    Customer is flexible on time (Time Not Set - date still required)
                  </Label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="scheduled_date">Date *</Label>
                    <Input
                      id="scheduled_date"
                      type="date"
                      value={scheduleData.scheduled_start ? scheduleData.scheduled_start.split('T')[0] : ''}
                      onChange={(e) => {
                        const time = scheduleData.scheduled_start?.split('T')[1] || '09:00';
                        handleStartTimeChange(`${e.target.value}T${time}`);
                      }}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="scheduled_time">Start Time {!scheduleData.flexible_timing && '*'}</Label>
                    <Input
                      id="scheduled_time"
                      type="time"
                      value={scheduleData.scheduled_start ? scheduleData.scheduled_start.split('T')[1]?.substring(0, 5) : ''}
                      onChange={(e) => {
                        const date = scheduleData.scheduled_start?.split('T')[0] || '';
                        handleStartTimeChange(`${date}T${e.target.value}`);
                      }}
                      disabled={scheduleData.flexible_timing}
                      required={!scheduleData.flexible_timing}
                      placeholder={scheduleData.flexible_timing ? "Flexible" : ""}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="manual_duration">Duration (minutes) {!scheduleData.flexible_timing && '*'}</Label>
                    <Input
                      id="manual_duration"
                      type="number"
                      min="0"
                      value={scheduleData.manual_duration}
                      onChange={(e) => handleDurationChange(e.target.value)}
                      placeholder="e.g., 60, 90, 120"
                      disabled={scheduleData.flexible_timing}
                      required={!scheduleData.flexible_timing}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {calculateTotalDuration(selectedOrder) > 0 
                        ? 'Auto-filled from service durations (editable)'
                        : 'Enter estimated job duration'
                      }
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="scheduled_end">End Time</Label>
                    <Input
                      id="scheduled_end"
                      type="time"
                      value={scheduleData.scheduled_end ? scheduleData.scheduled_end.split('T')[1]?.substring(0, 5) : ''}
                      onChange={(e) => {
                        const date = scheduleData.scheduled_end?.split('T')[0] || scheduleData.scheduled_start?.split('T')[0] || '';
                        setScheduleData({ ...scheduleData, scheduled_end: `${date}T${e.target.value}` });
                      }}
                      disabled={scheduleData.flexible_timing}
                      placeholder={scheduleData.flexible_timing ? "Flexible" : ""}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Auto-calculated from start + duration
                    </p>
                  </div>
                </div>
              </div>

              {/* Location Section */}
              <div className="space-y-3">
                <h3 className="font-medium text-sm">Location Details</h3>
                <div>
                  <Label htmlFor="location_type">Location Type</Label>
                  <Select
                    value={scheduleData.location_type}
                    onValueChange={(value) => {
                      setScheduleData({ ...scheduleData, location_type: value });
                      // Auto-populate customer address if mobile
                      if (value === 'mobile' && selectedOrder) {
                        const customer = customers.find(c => c.id === selectedOrder.customer_id);
                        if (customer) {
                          setScheduleData({
                            ...scheduleData,
                            location_type: value,
                            address_1: customer.address_1 || '',
                            address_2: customer.address_2 || '',
                            address_3: customer.address_3 || '',
                            city: customer.city || '',
                            county: customer.county || '',
                            postcode: customer.postcode || ''
                          });
                        }
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select location type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mobile">Mobile (Go to customer)</SelectItem>
                      <SelectItem value="onsite">On-site (Customer comes to us)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="scheduled_location">Location Name</Label>
                  <Input
                    id="scheduled_location"
                    value={scheduleData.scheduled_location}
                    onChange={(e) => setScheduleData({ ...scheduleData, scheduled_location: e.target.value })}
                    placeholder="e.g., Customer's driveway, Main workshop"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Address</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setScheduleData({
                        ...scheduleData,
                        address_1: '',
                        address_2: '',
                        address_3: '',
                        city: '',
                        county: '',
                        postcode: ''
                      })}
                      className="text-xs h-7"
                    >
                      Clear Address
                    </Button>
                  </div>
                  <Input
                    value={scheduleData.address_1}
                    onChange={(e) => setScheduleData({ ...scheduleData, address_1: e.target.value })}
                    placeholder="Address Line 1"
                  />
                  <Input
                    value={scheduleData.address_2}
                    onChange={(e) => setScheduleData({ ...scheduleData, address_2: e.target.value })}
                    placeholder="Address Line 2"
                  />
                  <Input
                    value={scheduleData.address_3}
                    onChange={(e) => setScheduleData({ ...scheduleData, address_3: e.target.value })}
                    placeholder="Address Line 3"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      value={scheduleData.city}
                      onChange={(e) => setScheduleData({ ...scheduleData, city: e.target.value })}
                      placeholder="City/Town"
                    />
                    <Input
                      value={scheduleData.county}
                      onChange={(e) => setScheduleData({ ...scheduleData, county: e.target.value })}
                      placeholder="County"
                    />
                  </div>
                  <Input
                    value={scheduleData.postcode}
                    onChange={(e) => setScheduleData({ ...scheduleData, postcode: e.target.value.toUpperCase() })}
                    placeholder="Postcode"
                    className="uppercase"
                  />
                </div>
              </div>

              {/* Instructions Section */}
              <div className="space-y-3">
                <h3 className="font-medium text-sm">Additional Information</h3>
                <div>
                  <Label htmlFor="special_instructions">Special Instructions</Label>
                  <textarea
                    id="special_instructions"
                    className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={scheduleData.special_instructions}
                    onChange={(e) => setScheduleData({ ...scheduleData, special_instructions: e.target.value })}
                    placeholder="Any special instructions for this job (parking, access codes, etc.)"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsScheduleDialogOpen(false);
                    setSelectedOrder(null);
                  }}
                  className="flex-1"
                >
                  Schedule Later
                </Button>
                <Button 
                  onClick={handleScheduleOrder} 
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  Schedule & Create Job
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print View */}
      {isPrintViewOpen && printOrder && (
        <div className="fixed inset-0 bg-white z-50 overflow-auto">
          <QuotePrintView
            quote={printOrder}
            onClose={() => {
              setIsPrintViewOpen(false);
              setPrintOrder(null);
            }}
            getAuthHeaders={getAuthHeaders}
          />
        </div>
      )}
    </div>
  );
};

export default OrdersManagement;
